import { useMemo, useCallback } from 'react';
import { countCustomFieldFilters } from '../../../shared/filterUtils';

/**
 * useFilterState Hook
 * 
 * Manages filter state logic for KanbanFilterDropdown including:
 * - Hide completed toggle (derived from status filter)
 * - Filter toggling for various filter types
 * - Custom field filter management
 * - Active filter counting
 */
export const useFilterState = ({
  filters,
  onFiltersChange,
  statuses,
  showActionableOnly,
}) => {
  // Get completed and non-completed status IDs for "Hide Completed" quick filter
  const { completedStatusIds, nonCompletedStatusIds } = useMemo(() => {
    const completed = statuses.filter(s => s.category === 'completed').map(s => s.id);
    const nonCompleted = statuses.filter(s => s.category !== 'completed').map(s => s.id);
    return { completedStatusIds: completed, nonCompletedStatusIds: nonCompleted };
  }, [statuses]);

  // Check if "hide completed" is effectively active
  const isHideCompletedActive = useMemo(() => {
    const currentStatusFilter = filters.status || [];
    if (currentStatusFilter.length === 0) return false;
    const hasAllNonCompleted = nonCompletedStatusIds.every(id => currentStatusFilter.includes(id));
    const hasNoCompleted = !completedStatusIds.some(id => currentStatusFilter.includes(id));
    return hasAllNonCompleted && hasNoCompleted && currentStatusFilter.length === nonCompletedStatusIds.length;
  }, [filters.status, completedStatusIds, nonCompletedStatusIds]);

  // Handle hide completed toggle
  const handleToggleHideCompleted = useCallback(() => {
    if (isHideCompletedActive) {
      onFiltersChange?.({ ...filters, status: [] });
    } else {
      onFiltersChange?.({ ...filters, status: [...nonCompletedStatusIds] });
    }
  }, [isHideCompletedActive, filters, onFiltersChange, nonCompletedStatusIds]);

  // Toggle filter value
  const toggleFilter = useCallback((filterType, value) => {
    const currentValues = filters[filterType] || [];
    const newValues = currentValues.includes(value)
      ? currentValues.filter(v => v !== value)
      : [...currentValues, value];
    onFiltersChange?.({ ...filters, [filterType]: newValues });
  }, [filters, onFiltersChange]);

  // Toggle custom field filter value
  const toggleCustomFieldFilter = useCallback((fieldId, value) => {
    const currentFilters = filters.customFields || {};
    const currentFieldFilter = currentFilters[fieldId] || { values: [] };
    const currentValues = currentFieldFilter.values || [];
    
    const newValues = currentValues.includes(value)
      ? currentValues.filter(v => v !== value)
      : [...currentValues, value];
    
    const newCustomFields = {
      ...currentFilters,
      [fieldId]: { ...currentFieldFilter, values: newValues },
    };
    
    onFiltersChange?.({ ...filters, customFields: newCustomFields });
  }, [filters, onFiltersChange]);

  // Update custom field text search
  const updateCustomFieldSearch = useCallback((fieldId, search) => {
    const currentFilters = filters.customFields || {};
    const currentFieldFilter = currentFilters[fieldId] || {};
    
    const newCustomFields = {
      ...currentFilters,
      [fieldId]: { ...currentFieldFilter, search },
    };
    
    onFiltersChange?.({ ...filters, customFields: newCustomFields });
  }, [filters, onFiltersChange]);

  // Update custom field number filter
  const updateCustomFieldNumber = useCallback((fieldId, operator, value) => {
    const currentFilters = filters.customFields || {};
    const currentFieldFilter = currentFilters[fieldId] || {};
    
    const newCustomFields = {
      ...currentFilters,
      [fieldId]: { ...currentFieldFilter, operator, value },
    };
    
    onFiltersChange?.({ ...filters, customFields: newCustomFields });
  }, [filters, onFiltersChange]);

  // Clear all filters
  const handleClearAll = useCallback(() => {
    onFiltersChange?.({});
  }, [onFiltersChange]);

  // Count custom field filters
  const customFieldFilterCount = useMemo(() => {
    return countCustomFieldFilters(filters.customFields);
  }, [filters.customFields]);

  // Count all active filters
  const activeFilterCount = useMemo(() => {
    const filterCount = Object.entries(filters).reduce((sum, [key, value]) => {
      if (key === 'customFields') return sum;
      return sum + (Array.isArray(value) ? value.length : 0);
    }, 0);
    return filterCount + customFieldFilterCount + (showActionableOnly ? 1 : 0);
  }, [filters, customFieldFilterCount, showActionableOnly]);

  return {
    isHideCompletedActive,
    handleToggleHideCompleted,
    toggleFilter,
    toggleCustomFieldFilter,
    updateCustomFieldSearch,
    updateCustomFieldNumber,
    handleClearAll,
    customFieldFilterCount,
    activeFilterCount,
  };
};
